import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, RefreshCw } from "lucide-react";
import { useSidebar } from "@/domains/core/layout/useSidebar";
import { CONTENT_PADDING_RIGHT } from "@/domains/core/layout/SidebarLayoutWidth";
import { useGlobalProgress } from "@/shared/contexts/use-global-progress";
import { ToastContainer } from "./header/NotificationCenter";
import { QuickActions } from "./header/QuickActions";
import { ProgressBar } from "./header/ProgressBar";
import { SpotifyOutlet } from "./header/SpotifyOutlet";
import { CalendarOutlet } from "./header/CalendarOutlet";
import { SmartHomeOutlet } from "./header/SmartHomeOutlet";

// Breadcrumb item interface
interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

export const Header = () => {
  const { isOpen: isSidebarOpen } = useSidebar();
  const { isAnySyncActive } = useGlobalProgress();
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);

  // Show search in header only when sidebar is collapsed
  const showHeaderSearch = !isSidebarOpen;

  // Generate breadcrumbs based on current route
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Handle dashboard route specifically
    if (location.pathname === "/dashboard") {
      breadcrumbs.push({ label: "Core", path: "/admin" });
      breadcrumbs.push({ label: "Dashboard", path: "/dashboard" });
      return breadcrumbs;
    }

    // Only show breadcrumbs if we have path segments
    if (pathSegments.length === 0) {
      return breadcrumbs;
    }

    // Add section based on route
    const currentPath = location.pathname;

    if (currentPath.startsWith("/admin")) {
      breadcrumbs.push({ label: "Core", path: "/admin" });

      // Add Admin Panel as base
      breadcrumbs.push({ label: "Admin Panel", path: "/admin" });

      // Add sub-route if we're on a specific admin tool
      const subSegments = pathSegments.slice(1); // Skip 'admin'
      if (subSegments.length > 0 && subSegments[0]) {
        const subRoute = subSegments[0];
        const subLabel = subRoute
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        breadcrumbs.push({ label: subLabel, path: currentPath });
      }

      return breadcrumbs;
    } else if (currentPath.startsWith("/nexus")) {
      breadcrumbs.push({ label: "Workspaces", path: "/nexus" });
    } else if (
      currentPath.startsWith("/investments") ||
      currentPath.startsWith("/trading") ||
      currentPath.startsWith("/responsumos") ||
      currentPath.startsWith("/real-estate") ||
      currentPath.startsWith("/smarthome")
    ) {
      breadcrumbs.push({ label: "Workspaces", path: "/investments" });
    } else if (currentPath.startsWith("/llm") || currentPath.startsWith("/settings")) {
      breadcrumbs.push({ label: "Support & Settings", path: "/settings" });
    }

    // Special handling for Oura tab navigation
    if (currentPath.startsWith("/nexus/health/oura")) {
      // Add the correct hierarchy for Oura
      breadcrumbs.push({ label: "Health", path: "/nexus/health" });
      breadcrumbs.push({ label: "Oura", path: "/nexus/health/oura" });

      // Add tab name if we're on a specific tab
      const tabSegments = pathSegments.slice(3); // Skip 'nexus', 'health', 'oura'
      if (tabSegments.length > 0 && tabSegments[0]) {
        const tabName = tabSegments[0]
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        breadcrumbs.push({ label: tabName, path: currentPath });
      }

      return breadcrumbs;
    }

    // Special handling for Settings tab navigation
    if (currentPath.startsWith("/settings")) {
      // Add Settings as base
      breadcrumbs.push({ label: "Settings", path: "/settings" });

      // Add tab name if we're on a specific tab
      const tabSegments = pathSegments.slice(1); // Skip 'settings'
      if (tabSegments.length > 0 && tabSegments[0]) {
        const tabName = tabSegments[0]
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        breadcrumbs.push({ label: tabName, path: currentPath });
      }

      return breadcrumbs;
    }

    // Special handling for ChatGPT
    if (currentPath.startsWith("/llm/chatgpt")) {
      breadcrumbs.push({ label: "ChatGPT", path: "/llm/chatgpt" });
    } else if (currentPath.startsWith("/llm/ollama")) {
      breadcrumbs.push({ label: "Ollama", path: "/llm/ollama" });
    } else if (currentPath.startsWith("/llm")) {
      breadcrumbs.push({ label: "LLM Services", path: "/llm" });
      return breadcrumbs;
    }

    // Special handling for individual Workspaces
    if (currentPath.startsWith("/investments")) {
      breadcrumbs.push({ label: "Investments", path: "/investments" });
      return breadcrumbs;
    }

    if (currentPath.startsWith("/trading")) {
      breadcrumbs.push({ label: "Trading", path: "/trading" });
      return breadcrumbs;
    }

    if (currentPath.startsWith("/smarthome")) {
      breadcrumbs.push({ label: "Smart Home", path: "/smarthome" });

      // Add sub-route if we're on a specific integration
      const subSegments = pathSegments.slice(1); // Skip 'smarthome'
      if (subSegments.length > 0 && subSegments[0]) {
        const subRoute = subSegments[0];
        const subLabel = subRoute
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        breadcrumbs.push({ label: subLabel, path: currentPath });
      }

      return breadcrumbs;
    }

    if (currentPath.startsWith("/responsumos")) {
      breadcrumbs.push({ label: "ResponsumOS", path: "/responsumos" });
      return breadcrumbs;
    }

    if (currentPath.startsWith("/real-estate")) {
      breadcrumbs.push({ label: "Real Estate", path: "/real-estate" });
      return breadcrumbs;
    }

    // Special handling for Admin Panel Tools
    if (
      currentPath.startsWith("/timetracq") ||
      currentPath.startsWith("/roadmap") ||
      currentPath.startsWith("/scripts")
    ) {
      breadcrumbs.push({ label: "Core", path: "/admin" });
      breadcrumbs.push({ label: "Admin Panel", path: "/admin" });

      // Add the specific tool
      if (currentPath.startsWith("/timetracq")) {
        breadcrumbs.push({ label: "TimeTracQ", path: "/timetracq" });

        // Add tab name if we're on a specific tab
        const tabSegments = pathSegments.slice(1); // Skip 'timetracq'
        if (tabSegments.length > 0 && tabSegments[0]) {
          const tabName = tabSegments[0]
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          breadcrumbs.push({ label: tabName, path: currentPath });
        }
      } else if (currentPath.startsWith("/roadmap")) {
        breadcrumbs.push({ label: "Roadmap", path: "/roadmap" });
      } else if (currentPath.startsWith("/scripts")) {
        breadcrumbs.push({ label: "Scripts", path: "/scripts" });
      }

      return breadcrumbs;
    }

    // Special handling for individual Scripts
    if (
      currentPath.startsWith("/git-tracking-manager") ||
      currentPath.startsWith("/branch-app") ||
      currentPath.startsWith("/git-cleanup")
    ) {
      breadcrumbs.push({ label: "Core", path: "/admin" });
      breadcrumbs.push({ label: "Admin Panel", path: "/admin" });
      breadcrumbs.push({ label: "Scripts", path: "/scripts" });

      // Add the specific script name
      if (currentPath.startsWith("/git-tracking-manager")) {
        breadcrumbs.push({
          label: "Git Tag Manager",
          path: "/git-tracking-manager",
        });
      } else if (currentPath.startsWith("/branch-app")) {
        breadcrumbs.push({ label: "Branch App", path: "/branch-app" });
      } else if (currentPath.startsWith("/git-cleanup")) {
        breadcrumbs.push({ label: "Git Cleanup", path: "/git-cleanup" });
      }

      return breadcrumbs;
    }

    // Handle other Oura routes (legacy routes)
    if (currentPath.startsWith("/health/oura") || currentPath.startsWith("/oura")) {
      // For legacy routes, just show the tab name
      const tabSegments = currentPath.startsWith("/health/oura")
        ? pathSegments.slice(2) // Skip 'health', 'oura'
        : pathSegments.slice(1); // Skip 'oura'

      if (tabSegments.length > 0 && tabSegments[0]) {
        const tabName = tabSegments[0]
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        breadcrumbs.push({ label: tabName, path: currentPath });
      }

      return breadcrumbs;
    }

    // Add route segments, but skip the first one if it matches the section
    let segmentPath = "";

    pathSegments.forEach((segment, index) => {
      segmentPath += `/${segment}`;

      // Skip first segment if it's already covered by the section
      if (index === 0) {
        const sectionLabel = breadcrumbs[0]?.label.toLowerCase();
        const segmentLabel = segment.toLowerCase();

        // Skip if this segment is already covered by the section
        if (sectionLabel === "core" && segmentLabel === "admin") return;
        if (
          sectionLabel === "workspaces" &&
          (segmentLabel === "nexus" ||
            segmentLabel === "investments" ||
            segmentLabel === "trading" ||
            segmentLabel === "responsumos" ||
            segmentLabel === "real-estate" ||
            segmentLabel === "smarthome")
        )
          return;
        if (
          sectionLabel === "support & settings" &&
          (segmentLabel === "chatgpt" || segmentLabel === "settings")
        )
          return;
      }

      // Convert segment to readable label
      const label = segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      breadcrumbs.push({
        label,
        path: segmentPath,
      });
    });

    return breadcrumbs;
  };

  // Handle search keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (showHeaderSearch && searchRef.current) {
          searchRef.current.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showHeaderSearch]);

  // Generate breadcrumbs from route
  const breadcrumbs = generateBreadcrumbs();

  return (
    <>
      {/* Global Progress Bar */}
      <ProgressBar />

      {/* Toast Notifications */}
      <ToastContainer />

      {/* Main Header */}
      <header
        className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-700/50 backdrop-blur-xl transition-all duration-300 shadow-lg shadow-slate-900/20"
        style={{
          paddingLeft: CONTENT_PADDING_RIGHT,
          paddingRight: CONTENT_PADDING_RIGHT,
        }}
      >
        {/* Left Section - Breadcrumbs & Page Title */}
        <div className="flex items-center gap-6">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <nav className="flex items-center space-x-2">
              {breadcrumbs.map((item, index) => (
                <div key={`${item.path}-${index}`} className="flex items-center">
                  {index > 0 && <ChevronRight className="w-4 h-4 text-neutral-600 mx-2" />}
                  <Link
                    to={item.path}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium text-slate-400 hover:text-slate-300 hover:bg-slate-800/30 transition-all duration-200"
                  >
                    {item.icon && item.icon}
                    {item.label}
                  </Link>
                </div>
              ))}
            </nav>
          )}
        </div>

        {/* Center Section - Empty when sidebar collapsed */}
        {showHeaderSearch && (
          <div className="flex-1 max-w-md mx-8">{/* Search is now in sidebar */}</div>
        )}

        {/* Right Section - Enhanced Actions */}
        <div className="flex items-center gap-3">
          {/* Quick Actions */}
          <QuickActions />

          {/* Sync Status */}
          {isAnySyncActive && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
              <span className="text-sm font-medium text-blue-400">Syncing...</span>
            </div>
          )}

          {/* Spotify Outlet */}
          <SpotifyOutlet />

          {/* Calendar Outlet */}
          <CalendarOutlet />

          {/* Smart Home Outlet */}
          <SmartHomeOutlet />
        </div>
      </header>
    </>
  );
};
